/* Gallery presentation only. The shared theme still owns variants, prices,
   zoom and cart actions; observe its gallery replacements and reordering. */
(() => {
  if (!document.body.classList.contains('template-product-campaign-lab')) return;
  const controllers = new Map();
  let galleryNumber = 0;

  const enhance = (root) => {
    if (controllers.has(root)) return;
    const stage = root.querySelector('[data-product-gallery]');
    const rail = root.querySelector('[data-gallery-thumbnails]');
    const controls = root.querySelector('[data-gallery-controls]');
    const status = root.querySelector('[data-gallery-status]');
    if (!stage || !rail || !controls) return;
    const abort = new AbortController();
    const events = { signal: abort.signal };
    const prefix = `CampaignMedia-${++galleryNumber}`;
    let slides = [];
    let buttons = [];
    let active = 0;
    let pointer = null;
    let suppressClickUntil = 0;

    const kind = (slide) => slide.dataset.galleryKind || (slide.querySelector('video') ? 'video' : 'image');
    const label = (slide) => kind(slide) === 'model' ? '3D view' : kind(slide).includes('video') ? 'Video' : 'Image';
    const size = (slide) => {
      const media = slide.querySelector('img, video');
      const width = media?.videoWidth || media?.naturalWidth || Number(media?.getAttribute('width'));
      const height = media?.videoHeight || media?.naturalHeight || Number(media?.getAttribute('height'));
      const ratio = width > 0 && height > 0 ? width / height : Number(slide.dataset.galleryRatio) || 1;
      slide.style.setProperty('--gallery-media-ratio', String(ratio));
    };
    const stop = (slide) => {
      slide.querySelectorAll('video').forEach(video => video.pause());
      slide.querySelectorAll('model-viewer').forEach(model => model.pause?.());
      // Removing an embedded player also stops its audio, without a provider API.
      if (slide.querySelector('[data-gallery-embed]')) slide.querySelectorAll('iframe').forEach(frame => frame.remove());
    };
    const select = (index, { focus = false, reveal = true } = {}) => {
      if (!slides.length) return;
      active = Math.max(0, Math.min(slides.length - 1, index));
      slides.forEach((slide, i) => {
        const selected = i === active;
        if (!selected) stop(slide);
        slide.hidden = !selected;
        slide.inert = !selected;
        slide.setAttribute('aria-hidden', String(!selected));
        if (selected) {
          slide.querySelectorAll('img').forEach(image => { image.loading = 'eager'; });
          const embed = slide.querySelector('[data-gallery-embed]');
          if (embed && !slide.querySelector('iframe')) slide.append(embed.content.cloneNode(true));
        }
        buttons[i].setAttribute('aria-pressed', String(selected));
        buttons[i].tabIndex = selected ? 0 : -1;
      });
      status.textContent = `${label(slides[active])} ${active + 1} of ${slides.length}`;
      if (reveal) {
        const button = buttons[active];
        const left = button.offsetLeft - rail.offsetLeft;
        if (left < rail.scrollLeft) rail.scrollLeft = left;
        else if (left + button.offsetWidth > rail.scrollLeft + rail.clientWidth) rail.scrollLeft = left + button.offsetWidth - rail.clientWidth;
      }
      if (focus) buttons[active].focus({ preventScroll: true });
    };
    const rebuild = (initial = false, variantChanged = false) => {
      const oldSlide = slides[active];
      const oldSlides = slides;
      const hadFocus = rail.contains(document.activeElement);
      slides = [...stage.children].filter(el => el.classList.contains('pdp-gallery__item'));
      oldSlides.filter(slide => !slides.includes(slide)).forEach(stop);
      const fragment = document.createDocumentFragment();
      buttons = slides.map((slide, index) => {
        size(slide);
        slide.id = `${prefix}-${index}`;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'campaign-product-gallery__thumbnail';
        button.dataset.gallerySelect = String(index);
        button.setAttribute('aria-label', `Show ${label(slide).toLowerCase()} ${index + 1} of ${slides.length}`);
        button.setAttribute('aria-controls', slide.id);
        const sourceImage = slide.querySelector('img');
        const source = slide.dataset.galleryThumbnail || sourceImage?.getAttribute('src');
        if (source) {
          const thumb = document.createElement('img');
          const url = new URL(source, document.baseURI);
          if (url.searchParams.has('width')) url.searchParams.set('width', '320');
          thumb.src = url.href;
          thumb.alt = '';
          thumb.width = 72;
          thumb.height = 72;
          thumb.loading = 'lazy';
          button.append(thumb);
        }
        if (kind(slide) !== 'image') {
          const badge = document.createElement('span');
          badge.className = 'campaign-product-gallery__type';
          badge.setAttribute('aria-hidden', 'true');
          badge.textContent = kind(slide) === 'model' ? '3D' : '▶';
          button.append(badge);
        }
        fragment.append(button);
        return button;
      });
      rail.replaceChildren(fragment);
      controls.hidden = slides.length < 2;
      root.classList.add('is-ready');
      const initialIndex = initial ? slides.findIndex(slide => slide.dataset.mediaId === root.dataset.initialMedia) : -1;
      const retainedIndex = slides.indexOf(oldSlide);
      select(initialIndex >= 0 ? initialIndex : variantChanged ? 0 : Math.max(0, retainedIndex), { focus: hadFocus, reveal: !initial });
    };
    root.addEventListener('click', event => {
      const thumb = event.target.closest('[data-gallery-select]');
      if (thumb) select(Number(thumb.dataset.gallerySelect));
    }, events);
    rail.addEventListener('keydown', event => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const target = event.key === 'Home' ? 0 : event.key === 'End' ? slides.length - 1 : active + (event.key === 'ArrowRight' ? 1 : -1);
      select(target, { focus: true });
    }, events);
    stage.addEventListener('pointerdown', event => {
      if (event.button !== 0 || !event.target.closest('[data-product-zoom]')) return;
      pointer = { x: event.clientX, y: event.clientY, id: event.pointerId };
    }, events);
    stage.addEventListener('pointerup', event => {
      if (!pointer || event.pointerId !== pointer.id) return;
      const dx = event.clientX - pointer.x;
      const dy = event.clientY - pointer.y;
      pointer = null;
      if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.3) return;
      suppressClickUntil = Date.now() + 350;
      select(active + (dx < 0 ? 1 : -1));
    }, events);
    stage.addEventListener('pointercancel', () => { pointer = null; }, events);
    stage.addEventListener('dragstart', event => event.preventDefault(), events);
    const resizeLoadedMedia = (event) => {
      const slide = event.target.closest?.('.pdp-gallery__item');
      if (slide) size(slide);
    };
    stage.addEventListener('load', resizeLoadedMedia, { ...events, capture: true });
    stage.addEventListener('loadedmetadata', resizeLoadedMedia, { ...events, capture: true });
    stage.addEventListener('click', event => {
      if (Date.now() >= suppressClickUntil) return;
      event.preventDefault();
      event.stopPropagation();
    }, { ...events, capture: true });
    stage.addEventListener('error', event => {
      const media = event.target;
      const slide = media.closest?.('.pdp-gallery__item');
      if (!slide?.isConnected || !['IMG', 'VIDEO'].includes(media.tagName) || slide.querySelector('.campaign-product-gallery__error')) return;
      const message = document.createElement('p');
      message.className = 'campaign-product-gallery__error';
      message.setAttribute('role', 'status');
      message.textContent = `${media.tagName === 'VIDEO' ? 'Video' : 'Image'} unavailable. Please try another view.`;
      slide.append(message);
    }, { ...events, capture: true });
    const observer = new MutationObserver(records => {
      const childrenChanged = records.some(record => record.type === 'childList');
      const variantChanged = records.some(record => record.type === 'attributes');
      if (childrenChanged || variantChanged) rebuild(false, variantChanged);
    });
    // Only watch the stage itself; thumbnail/selection updates cannot loop.
    observer.observe(stage, { childList: true, attributes: true, attributeFilter: ['data-gallery-mode', 'data-gallery-variant'] });
    rebuild(true);
    controllers.set(root, () => { observer.disconnect(); abort.abort(); slides.forEach(stop); });
  };
  const init = () => document.querySelectorAll('[data-campaign-product-gallery]').forEach(enhance);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', event => {
    controllers.forEach((cleanup, root) => {
      if (!event.target.contains(root)) return;
      cleanup();
      controllers.delete(root);
    });
  });

  // Keep the selected lightbox thumbnail visible when its rail needs to scroll.
  const modal = document.querySelector('[data-product-modal-content]');
  if (modal) {
    const revealSelectedPhoto = () => {
      if (!modal.matches('.modal-card--gallery:not(.modal-card--review)')) return;
      const button = modal.querySelector('.product-gallery-viewer__thumb.is-selected');
      const rail = button?.parentElement;
      if (!rail) return;
      const item = button.getBoundingClientRect();
      const bounds = rail.getBoundingClientRect();
      if (item.top < bounds.top) rail.scrollTop += item.top - bounds.top;
      else if (item.bottom > bounds.bottom) rail.scrollTop += item.bottom - bounds.bottom;
      if (item.left < bounds.left) rail.scrollLeft += item.left - bounds.left;
      else if (item.right > bounds.right) rail.scrollLeft += item.right - bounds.right;
    };
    new MutationObserver(revealSelectedPhoto).observe(modal, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    modal.addEventListener('load', revealSelectedPhoto, true);
    if ('ResizeObserver' in window) new ResizeObserver(revealSelectedPhoto).observe(modal);
  }
})();
